import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal, EASE } from "@/lib/motion";
import { CTABand } from "@/components/site/Bands";
import { CASE_STUDIES, RESULTS } from "@/data/site";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SEO, breadcrumbSchema } from "@/components/site/SEO";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Case Studies", path: "/case-studies" },
];

const PageHero = () => (
  <section data-testid="cases-hero" className="pt-6 pb-16 md:pb-20 bg-white">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <p className="text-xs uppercase tracking-[0.25em] text-[#999999] mb-8 font-mono">Case Studies</p>
      <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight text-[#111111]">
        <MaskedLines lines={["Real Results.", "Real Companies."]} delay={0.1} />
        <span className="reveal-line">
          <motion.span
            className="block italic"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 + 2 * 0.12 }}
          >
            Real Metrics.
          </motion.span>
        </span>
      </h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.7 }}
        className="mt-8 max-w-2xl text-lg md:text-xl text-[#666666] leading-relaxed"
      >
        B2B SaaS companies trust Upword Media to build content engines that drive pipeline. Here&apos;s how
        we&apos;ve helped them grow organic traffic, rankings, and qualified leads.
      </motion.p>
    </div>
  </section>
);

const Aggregate = () => (
  <section className="pb-8 bg-white" data-testid="cases-aggregate" aria-labelledby="agg-heading">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <h2 id="agg-heading" className="sr-only">Aggregate results</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#E0E0E0]">
        {RESULTS.map((r, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <div className="p-8 border-r border-b border-[#E0E0E0] h-full">
              <p className="font-serif text-5xl md:text-6xl text-[#111111] leading-none">{r.stat}</p>
              <p className="mt-5 text-sm text-[#666666] leading-relaxed">{r.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function CaseStudies() {
  const [active, setActive] = useState(null);

  return (
    <div data-testid="case-studies-page">
      <SEO
        title="Case Studies — Real B2B SaaS Content Results"
        description="How Upword Media helped B2B SaaS brands like Capillary Technologies, Airmeet, inFeedo and JustCall grow organic traffic, rankings, and qualified pipeline."
        path="/case-studies"
        jsonLd={[breadcrumbSchema(CRUMBS)]}
      />
      <Breadcrumbs items={CRUMBS} />
      <PageHero />
      <Aggregate />

      <section className="section-pad bg-white" aria-labelledby="featured-cases">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <h2 id="featured-cases" className="sr-only">Featured case studies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {CASE_STUDIES.map((c, i) => (
              <motion.button
                key={c.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.08, ease: EASE }}
                onClick={() => setActive(c)}
                data-testid={`case-card-${c.id}`}
                className="group text-left border border-[#E0E0E0] p-8 md:p-10 bg-white hover:bg-[#111111] transition-colors duration-300"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#999999] group-hover:text-[#666666]">
                    {c.tag}
                  </span>
                  <ArrowUpRight size={22} className="text-[#999999] group-hover:text-white transition-all group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
                <h3 className="mt-8 font-serif text-3xl md:text-4xl text-[#111111] group-hover:text-white transition-colors">
                  {c.client}
                </h3>
                <p className="mt-4 text-[#666666] group-hover:text-[#E0E0E0] transition-colors leading-relaxed line-clamp-2">
                  {c.result}
                </p>
                <div className="mt-8 flex gap-10">
                  {c.metrics.map((m) => (
                    <div key={m.l}>
                      <p className="font-serif text-3xl text-[#111111] group-hover:text-white transition-colors">{m.v}</p>
                      <p className="text-xs text-[#999999] group-hover:text-[#999999] mt-1">{m.l}</p>
                    </div>
                  ))}
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto rounded-none border-[#111111] p-8 md:p-12" data-testid="case-dialog">
          {active && (
            <>
              <DialogHeader>
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#999999] text-left">{active.tag}</p>
                <DialogTitle className="font-serif text-4xl md:text-5xl text-[#111111] text-left mt-2">
                  {active.client}
                </DialogTitle>
              </DialogHeader>
              <p className="text-[#666666] leading-relaxed mt-2">{active.context}</p>

              <div className="mt-8 space-y-8">
                {[
                  ["Challenge", active.challenge],
                  ["Solution", active.solution],
                  ["Result", active.result],
                ].map(([label, body]) => (
                  <div key={label} className="border-t border-[#E0E0E0] pt-5">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#999999] mb-3">{label}</p>
                    <p className="text-[#111111] text-lg leading-relaxed">{body}</p>
                  </div>
                ))}
              </div>

              {active.quote && (
                <figure className="mt-10 bg-[#111111] text-white p-8">
                  <blockquote className="font-serif text-xl md:text-2xl italic leading-[1.4]">
                    &ldquo;{active.quote.text}&rdquo;
                  </blockquote>
                  <figcaption className="mt-6 text-sm">
                    <span className="text-white font-semibold">{active.quote.name}</span>
                    <span className="text-[#999999]"> — {active.quote.role}</span>
                  </figcaption>
                </figure>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <CTABand heading="Your growth story could be next." showSecondary={false} showCaseStudies={false} />
    </div>
  );
}
